import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart3, Users, Ticket, Film, Star, Heart, Eye, DollarSign,
  Tv, Radio, Trophy, Loader2, ArrowUpRight, Activity, Clock,
} from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

function fmt(n) {
  if (n === undefined || n === null) return "—";
  if (typeof n !== "number") return String(n);
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
function money(n, ccy = "$") {
  if (!n) return `${ccy}0`;
  return `${ccy}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function KpiCard({ icon: Icon, label, value, sub, accent = "red", testId, live = false }) {
  const accents = {
    red: "border-[#E50914]/30 bg-[#E50914]/5 text-[#E50914]",
    gold: "border-[#f6c34a]/40 bg-[#f6c34a]/5 text-[#f6c34a]",
    cyan: "border-cyan-500/30 bg-cyan-500/5 text-cyan-400",
    violet: "border-violet-500/30 bg-violet-500/5 text-violet-400",
  };
  return (
    <div
      data-testid={testId}
      className={`relative rounded-xl border ${accents[accent].split(" ")[0]} bg-[#0e0e0e] p-5 overflow-hidden transition hover:-translate-y-0.5`}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none opacity-20"
           style={{ background: `radial-gradient(circle, currentColor 0%, transparent 70%)`, color: accent === "red" ? "#E50914" : accent === "gold" ? "#f6c34a" : "#22d3ee" }} />
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {live && (
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#E50914]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E50914] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E50914]"></span>
            </span>
            Live
          </div>
        )}
      </div>
      <div className="text-3xl font-black text-white tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {value}
      </div>
      <div className="text-xs text-gray-400 mt-1 uppercase tracking-widest">{label}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-2">{sub}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 mt-12 mb-5">
      <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#f6c34a]" />
      </div>
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight">{title}</h2>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [content, setContent] = useState(null);
  const [producer, setProducer] = useState(null);
  const [channels, setChannels] = useState(null);
  const [liveTickets, setLiveTickets] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: meData } = await api.get("/dashboard/me");
        if (cancelled) return;
        setMe(meData);
        // Always pull content metrics (filtered server-side)
        if (meData.is_content_owner) {
          api.get("/dashboard/content").then(r => !cancelled && setContent(r.data)).catch(() => {});
        }
        if (meData.is_filmmaker || meData.is_admin) {
          api.get("/dashboard/producer").then(r => !cancelled && setProducer(r.data)).catch(() => {});
        }
        if (meData.is_admin) {
          api.get("/dashboard/channels").then(r => !cancelled && setChannels(r.data)).catch(() => {});
          api.get("/dashboard/live-tickets").then(r => !cancelled && setLiveTickets(r.data)).catch(() => {});
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load dashboard");
      }
    };
    load();
    // Live polling for the headline KPIs every 12 seconds
    const t = setInterval(() => {
      if (cancelled) return;
      api.get("/dashboard/live-tickets").then(r => !cancelled && setLiveTickets(r.data)).catch(() => {});
      api.get("/dashboard/channels").then(r => !cancelled && setChannels(r.data)).catch(() => {});
    }, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 max-w-xl mx-auto px-6 text-center">
          <h1 className="text-3xl font-black mb-3">Dashboard unavailable</h1>
          <p className="text-gray-400 mb-6">{err}</p>
          <button onClick={() => nav("/browse")} className="btn-red px-6 py-2.5 rounded">Back to Browse</button>
        </div>
        <Footer />
      </div>
    );
  }
  if (!me) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#E50914]" />
        </div>
      </div>
    );
  }

  const hasAnySection = me.is_content_owner || me.is_filmmaker || me.is_admin;
  if (!hasAnySection) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 max-w-xl mx-auto px-6 text-center">
          <Trophy className="w-12 h-12 text-[#f6c34a] mx-auto mb-4" />
          <h1 className="text-3xl font-black mb-3">No dashboard yet</h1>
          <p className="text-gray-400 mb-6">
            Submit a film to the festival or upload your own content to unlock a creator dashboard with analytics, ticket sales, and audience insights.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/creator/apply" className="btn-red px-6 py-2.5 rounded">Apply to become a creator</Link>
            <Link to="/festival/submit" className="btn-glass px-6 py-2.5 rounded">Submit a film instead</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-24 px-6 lg:px-12 max-w-[1600px] mx-auto pb-20" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#f6c34a] mb-2 flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" /> Creator Dashboard
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight" data-testid="dashboard-heading">
              Welcome back, {me.name}.
            </h1>
            <div className="flex gap-2 mt-3">
              {me.is_admin && <span className="px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded bg-[#E50914]/15 border border-[#E50914]/30 text-[#E50914]">Admin</span>}
              {me.is_filmmaker && <span className="px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded bg-amber-500/15 border border-amber-500/30 text-amber-400">Filmmaker</span>}
              {me.is_channel_owner && <span className="px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">Channel Owner</span>}
              {me.is_content_owner && <span className="px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded bg-violet-500/15 border border-violet-500/30 text-violet-400">Content Owner</span>}
            </div>
          </div>
        </div>

        {/* Headline KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {me.is_admin && (
            <KpiCard
              testId="kpi-live-watching"
              icon={Activity}
              label="Watching with paid tickets · now"
              value={fmt(liveTickets?.totals?.currently_watching ?? 0)}
              sub={`${fmt(liveTickets?.totals?.ticket_holders_24h ?? 0)} ticket-holders in last 24h`}
              accent="red"
              live
            />
          )}
          {me.is_admin && (
            <KpiCard
              testId="kpi-rev-7d"
              icon={DollarSign}
              label="Ticket revenue · last 7 days"
              value={money(channels?.totals?.ticket_revenue_7d_total ?? 0)}
              sub={`${fmt(channels?.totals?.paid_tickets_7d_total ?? 0)} tickets sold`}
              accent="gold"
            />
          )}
          {me.is_content_owner && (
            <KpiCard
              testId="kpi-content-plays"
              icon={Eye}
              label="Total plays"
              value={fmt(content?.totals?.plays ?? 0)}
              sub={`${fmt(content?.totals?.total_minutes_watched ?? 0)} minutes watched`}
              accent="cyan"
            />
          )}
          {me.is_content_owner && (
            <KpiCard
              testId="kpi-content-completion"
              icon={Star}
              label="Completion rate"
              value={`${content?.totals?.completion_rate ?? 0}%`}
              sub={`${fmt(content?.totals?.reviews ?? 0)} reviews · ${fmt(content?.totals?.watchlist_adds ?? 0)} watchlist adds`}
              accent="violet"
            />
          )}
          {!me.is_admin && me.is_filmmaker && (
            <>
              <KpiCard testId="kpi-prod-views" icon={Eye} label="Submission views" value={fmt(producer?.totals?.total_views ?? 0)} accent="cyan" />
              <KpiCard testId="kpi-prod-votes" icon={Heart} label="Audience votes" value={fmt(producer?.totals?.total_votes ?? 0)} accent="red" />
              <KpiCard testId="kpi-prod-rev" icon={DollarSign} label="Ticket revenue" value={money(producer?.totals?.total_ticket_revenue ?? 0)} accent="gold" />
              <KpiCard testId="kpi-prod-followers" icon={Users} label="Followers" value={fmt(producer?.totals?.followers ?? 0)} accent="violet" />
            </>
          )}
        </div>

        {/* Revenue trend chart (all creators + admin) */}
        <RevenueTrendSection />

        {/* Live ticketed shows (admin only) */}
        {me.is_admin && liveTickets?.shows?.length > 0 && (
          <>
            <SectionHeader icon={Ticket} title="Live ticketed shows" sub="Recent paid screenings — currently watching counts update every 12s" />
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#0e0e0e]">
              <table className="w-full text-sm" data-testid="dashboard-live-tickets-table">
                <thead className="text-xs uppercase tracking-widest text-gray-500 bg-black/40">
                  <tr>
                    <th className="text-left px-4 py-3">Show</th>
                    <th className="text-left px-4 py-3">Channel</th>
                    <th className="text-right px-4 py-3">Tickets</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                    <th className="text-right px-4 py-3">Watching now</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {liveTickets.shows.map((s) => (
                    <tr key={s.schedule_id} className="border-t border-white/5 hover:bg-white/[0.02] transition">
                      <td className="px-4 py-3 flex items-center gap-3">
                        {s.poster && <img src={s.poster} alt="" className="w-9 h-12 object-cover rounded" />}
                        <span className="font-semibold">{s.show_title}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{s.channel_name}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold">{fmt(s.ticket_count)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#f6c34a] font-bold">{money(s.revenue)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className={`font-bold ${s.currently_watching > 0 ? "text-[#E50914]" : "text-gray-500"}`}>
                          {fmt(s.currently_watching)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/live/${s.channel_slug}`} className="text-[#f6c34a] hover:text-white inline-flex items-center gap-1 text-xs">
                          Open <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Channels (admin) */}
        {me.is_admin && channels?.channels?.length > 0 && (
          <>
            <SectionHeader icon={Radio} title="Channels" sub="Live viewers + 7-day ticket performance per channel" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-channels">
              {channels.channels.map((c) => (
                <Link
                  key={c.channel_id}
                  to={`/live/${c.slug}`}
                  className="block rounded-xl border border-white/10 bg-[#0e0e0e] hover:border-[#E50914]/40 transition p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                           style={{ background: `${c.color || "#E50914"}22`, color: c.color || "#E50914" }}>
                        <Tv className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-bold">{c.name}</div>
                        <div className="text-xs text-gray-500">@{c.slug}</div>
                      </div>
                    </div>
                    {c.live_viewers > 0 && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#E50914] flex items-center gap-1">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E50914] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E50914]"></span>
                        </span>
                        {c.live_viewers}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-lg font-black tabular-nums">{fmt(c.schedules)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">Schedules</div>
                    </div>
                    <div>
                      <div className="text-lg font-black tabular-nums">{fmt(c.paid_tickets_7d)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">Tickets · 7d</div>
                    </div>
                    <div>
                      <div className="text-lg font-black tabular-nums text-[#f6c34a]">{money(c.ticket_revenue_7d)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">Revenue · 7d</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* My Content */}
        {me.is_content_owner && content?.videos?.length > 0 && (
          <>
            <SectionHeader icon={Film} title="My content" sub={`${content.totals.videos} title${content.totals.videos === 1 ? "" : "s"} — sorted by plays`} />
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#0e0e0e]">
              <table className="w-full text-sm" data-testid="dashboard-content-table">
                <thead className="text-xs uppercase tracking-widest text-gray-500 bg-black/40">
                  <tr>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-right px-4 py-3">Plays</th>
                    <th className="text-right px-4 py-3">Watch time</th>
                    <th className="text-right px-4 py-3">Completion</th>
                    <th className="text-right px-4 py-3">Watchlist</th>
                    <th className="text-right px-4 py-3">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {content.videos.slice(0, 25).map((v) => {
                    const cr = v.plays ? Math.round((v.finishes / v.plays) * 100) : 0;
                    return (
                      <tr key={v.id} className="border-t border-white/5 hover:bg-white/[0.02] transition">
                        <td className="px-4 py-3 flex items-center gap-3">
                          {v.poster && <img src={v.poster} alt="" className="w-9 h-12 object-cover rounded" />}
                          <div>
                            <Link to={`/title/${v.id}`} className="font-semibold hover:text-[#f6c34a]">{v.title}</Link>
                            <div className="text-xs text-gray-500">{v.type} · {v.year}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-bold">{fmt(v.plays)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-300">{fmt(v.total_minutes_watched)}m</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          <span className={cr >= 70 ? "text-green-400" : cr >= 30 ? "text-amber-400" : "text-gray-400"}>
                            {cr}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{fmt(v.watchlist_adds)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {v.avg_rating > 0 ? (
                            <span className="text-[#f6c34a] font-bold">★ {v.avg_rating.toFixed(1)}</span>
                          ) : <span className="text-gray-500">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Producer / Filmmaker section */}
        {(me.is_filmmaker || me.is_admin) && producer?.submissions?.length > 0 && (
          <>
            <SectionHeader icon={Trophy} title="Festival submissions" sub={`${producer.totals.submissions} submission${producer.totals.submissions === 1 ? "" : "s"} — ${producer.totals.approved} approved`} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <KpiCard testId="kpi-sub-views" icon={Eye} label="Total views" value={fmt(producer.totals.total_views)} accent="cyan" />
              <KpiCard testId="kpi-sub-votes" icon={Heart} label="Total votes" value={fmt(producer.totals.total_votes)} accent="red" />
              <KpiCard testId="kpi-sub-tickets" icon={Ticket} label="Audience tickets" value={fmt(producer.totals.total_audience_tickets)} accent="violet" />
              <KpiCard testId="kpi-sub-revenue" icon={DollarSign} label="Ticket revenue" value={money(producer.totals.total_ticket_revenue)} accent="gold" />
            </div>
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#0e0e0e]">
              <table className="w-full text-sm" data-testid="dashboard-producer-table">
                <thead className="text-xs uppercase tracking-widest text-gray-500 bg-black/40">
                  <tr>
                    <th className="text-left px-4 py-3">Submission</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Views</th>
                    <th className="text-right px-4 py-3">Votes</th>
                    <th className="text-right px-4 py-3">Rating</th>
                    <th className="text-right px-4 py-3">Tickets</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {producer.submissions.slice(0, 25).map((s) => (
                    <tr key={s.submission_id} className="border-t border-white/5 hover:bg-white/[0.02] transition">
                      <td className="px-4 py-3">
                        <div className="font-semibold">{s.title}</div>
                        <div className="text-xs text-gray-500">{s.fee_tier?.toUpperCase()} · {s.runtime_min} min · {s.region}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={
                          s.status === "approved" ? "text-green-400" :
                          s.status === "pending_payment" ? "text-amber-400" :
                          s.status === "rejected" ? "text-red-400" :
                          "text-gray-400"
                        }>{s.status?.replace("_", " ")}</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmt(s.view_count)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmt(s.votes)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{s.avg_rating > 0 ? <span className="text-[#f6c34a] font-bold">★ {s.avg_rating.toFixed(1)}</span> : <span className="text-gray-500">—</span>}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmt(s.audience_tickets)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#f6c34a] font-bold">{money(s.ticket_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="text-xs text-gray-500 text-center mt-12">
          <Clock className="w-3 h-3 inline mr-1" /> Updated every 12 seconds · live data
        </div>
      </main>
      <Footer />
    </div>
  );
}


/* ---------------- RevenueTrendSection: daily area chart + breakdown ---------------- */
function RevenueTrendSection() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/dashboard/revenue-trend?days=${days}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => console.warn("[dashboard] revenue-trend failed", e?.response?.status))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (!data) {
    return (
      <div className="mt-12 p-8 rounded-xl border border-white/10 bg-[#0e0e0e] text-center text-gray-500 text-sm" data-testid="dashboard-rev-trend-loading">
        Loading revenue trend…
      </div>
    );
  }

  const maxTotal = Math.max(1, ...data.by_day.map(d => d.total));
  const chartWidth = 100; // svg viewBox is percentage-style
  const stepX = chartWidth / Math.max(1, data.by_day.length - 1);

  // Build SVG polyline points for total
  const points = data.by_day.map((d, i) => {
    const x = i * stepX;
    const y = 100 - (d.total / maxTotal) * 95;  // leave a top margin
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const areaPath = `M 0,100 L ${points.split(" ").map(p => p).join(" L ")} L ${chartWidth},100 Z`;

  return (
    <div className="mt-12" data-testid="dashboard-rev-trend">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-[#f6c34a]" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight">Revenue trend</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {data.is_admin_view ? "Platform-wide" : "Your earnings"} · last {days} days
            </p>
          </div>
        </div>
        <div className="flex gap-1 bg-black/40 rounded p-1 border border-white/5 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`rev-trend-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded font-semibold transition-colors ${days === d ? "bg-[#E50914] text-white" : "text-gray-400 hover:text-white"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Total summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <RevCell label="Grand total" value={data.totals.grand_total} accent="gold" highlight />
        <RevCell label="GCR tickets" value={data.totals.gcr_tickets} />
        <RevCell label="Festival tickets" value={data.totals.festival_tickets} />
        <RevCell label="Submission fees" value={data.totals.fees} />
      </div>

      {/* SVG area chart */}
      <div className="bg-[#0e0e0e] border border-white/10 rounded-xl p-5">
        {loading && <div className="text-[11px] text-gray-500 mb-2">Refreshing…</div>}
        <div className="relative h-56" data-testid="rev-trend-chart">
          {data.by_day.every(d => d.total === 0) ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
              No revenue in the last {days} days yet. Keep going.
            </div>
          ) : (
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
              <defs>
                <linearGradient id="rev-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E50914" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#E50914" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {/* Horizontal guide lines */}
              {[25, 50, 75].map(y => (
                <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="0.2" />
              ))}
              <path d={areaPath} fill="url(#rev-gradient)" />
              <polyline points={points} fill="none" stroke="#E50914" strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
              {/* Data point dots on hover targets */}
              {data.by_day.map((d, i) => {
                const x = i * stepX;
                const y = 100 - (d.total / maxTotal) * 95;
                return (
                  <g key={d.date}>
                    <circle cx={x} cy={y} r="0.6" fill="#fff" opacity={d.total > 0 ? 1 : 0.2} />
                    <title>{`${d.date}: $${d.total.toFixed(2)}`}</title>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
        {/* X-axis labels (just first / last / middle) */}
        <div className="flex justify-between text-[10px] text-gray-500 mt-2 px-1">
          <span>{data.by_day[0]?.date}</span>
          <span>{data.by_day[Math.floor(data.by_day.length / 2)]?.date}</span>
          <span>{data.by_day[data.by_day.length - 1]?.date}</span>
        </div>
      </div>

      {/* Top titles by revenue */}
      {data.top_titles_by_revenue.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-3">Top earning titles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="rev-top-titles">
            {data.top_titles_by_revenue.map((t, i) => (
              <div key={`${t.kind}-${t.title}-${i}`} className="bg-[#0e0e0e] border border-white/10 rounded-lg p-3 flex items-start gap-3">
                {t.poster ? (
                  <img src={t.poster} alt="" className="w-12 h-16 object-cover rounded shrink-0" />
                ) : (
                  <div className="w-12 h-16 rounded bg-white/5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-0.5">#{i + 1} · {t.kind}</div>
                  <div className="font-bold text-sm line-clamp-2 leading-tight">{t.title}</div>
                  <div className="text-[#f6c34a] text-sm font-bold mt-1 tabular-nums">{money(t.revenue)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RevCell({ label, value, accent, highlight }) {
  const tone = highlight ? "from-[#1a1308] to-[#0f0a04] border-amber-600/30" : "from-[#0e0e0e] to-[#0a0a0a] border-white/10";
  const valTone = accent === "gold" ? "text-[#f6c34a]" : "text-white";
  return (
    <div className={`rounded-lg p-4 bg-gradient-to-br border ${tone}`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{label}</div>
      <div className={`text-2xl font-black tabular-nums ${valTone}`}>{money(value)}</div>
    </div>
  );
}
